<template>
  <div class="cross-option-rules-editor">
    <EmptyState
      v-if="!localRules.length"
      title="Nog geen cross-option rules"
      description="Voeg regels toe voor geavanceerde pricing zoals staffels, bundels en conditionele kortingen"
    >
      <Btn variant="secondary" @click="addRule" icon="plus">Eerste Regel Toevoegen</Btn>
    </EmptyState>

    <div v-else class="rules-list">
      <draggable
        v-model="localRules"
        item-key="id"
        handle=".rule-drag-handle"
        class="rules-draggable"
        :animation="150"
        ghost-class="rule-card--ghost"
      >
        <template #item="{ element: rule, index: idx }">
          <div class="rule-card" :class="{ 'rule-card--collapsed': !isExpanded(rule.id) }">
            <div class="rule-header" @click="toggleRule(rule.id)">
              <div
                class="rule-drag-handle"
                title="Sleep om de volgorde te wijzigen"
                aria-label="Sleep om de volgorde te wijzigen"
                @click.stop
              >
                <BaseIcon name="grip-dots" size="12" />
              </div>
              <div class="rule-info" @click.stop>
                <InplaceEdit
                  :id="`rule-label-${idx}`"
                  v-model="rule.label"
                  placeholder="Klik om naam in te voeren..."
                  :autoFocus="rule.id === newlyAddedRuleId"
                  class="rule-label-input"
                />
                <ToggleInput
                  :id="`rule-show-quote-${idx}`"
                  label="Toon op offerte"
                  v-model="rule.showInQuote"
                />
              </div>
              <div class="rule-header-actions" @click.stop>
                <Btn
                  icon="trash"
                  variant="outlined"
                  size="lg"
                  @click="removeRule(idx)"
                  title="Verwijder regel"
                />
              </div>
              <BaseIcon name="chevron" size="14" class="rule-chevron" />
            </div>

            <div
              v-if="!isExpanded(rule.id)"
              class="rule-collapsed-summary"
              @click="toggleRule(rule.id)"
            >
              <span class="rule-collapsed-summary__condition">
                <strong>Als:</strong> {{ formatConditionPreview(rule.rootGroup) }}
              </span>
              <span class="rule-collapsed-summary__action">
                <strong>Dan:</strong> {{ formatActionPreview(rule.actions) }}
              </span>
            </div>

            <div v-show="isExpanded(rule.id)" class="rule-body">
              <!-- Conditions Section -->
              <div class="rule-section">
                <h5>Voorwaarden</h5>
                <div class="conditions-wrapper">
                  <CPQConditionGroup
                    :group="rule.rootGroup"
                    :attributes="availableAttributes"
                    :canRemove="false"
                  />
                </div>
              </div>

              <!-- Actions Section -->
              <div class="rule-section">
                <h5>Acties</h5>
                <div class="actions-list">
                  <div v-for="(action, aidx) in rule.actions" :key="aidx" class="action-row">
                    <SelectInput
                      :id="`action-type-${idx}-${aidx}`"
                      label="Actie type"
                      v-model="action.type"
                      :options="actionTypeOptions"
                      class="action-type-select"
                    />

                    <div v-if="action.type !== 'formula'" class="action-value-field">
                      <NumberInput
                        :id="`action-value-${idx}-${aidx}`"
                        :label="getActionValueLabel(action.type)"
                        v-model="action.value"
                        :min="getActionValueMin(action.type)"
                        :max="getActionValueMax(action.type)"
                        :step="getActionValueStep(action.type)"
                        class="action-value-input"
                      />
                      <p v-if="getActionHelperText(action)" class="action-help">
                        {{ getActionHelperText(action) }}
                      </p>
                    </div>

                    <div v-else-if="action.type === 'formula'" class="formula-input-wrapper">
                      <FormulaEditor
                        :id="`action-formula-${idx}-${aidx}`"
                        label="Formule"
                        v-model="action.expression"
                        :custom-fields="formulaFields"
                        :show-conditional-operators="false"
                        compact
                      />
                    </div>

                    <Btn
                      v-if="rule.actions.length > 1"
                      icon="minus"
                      variant="plain"
                      size="sm"
                      @click="removeAction(idx, aidx)"
                      title="Verwijder actie"
                    />
                  </div>

                  <Btn variant="dashed" size="sm" icon="plus" @click="addAction(idx)">
                    Actie toevoegen
                  </Btn>
                </div>
              </div>

              <!-- Preview Section -->
              <div class="rule-section rule-preview">
                <h5>Preview</h5>
                <div class="preview-content">
                  <div class="preview-condition">
                    <strong>Als:</strong>
                    {{ formatConditionPreview(rule.rootGroup) }}
                  </div>
                  <div class="preview-action">
                    <strong>Dan:</strong>
                    {{ formatActionPreview(rule.actions) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </draggable>

      <Btn variant="dashed" @click="addRule" icon="plus" class="add-rule-btn">Regel toevoegen</Btn>
    </div>
  </div>
</template>

<script>
  import { ref, reactive, computed, watch, nextTick } from 'vue';
  import { uid } from 'uid';
  import draggable from 'vuedraggable';

  // Import existing CPQ components
  import CPQConditionGroup from '@/pages/flow/CPQConditionGroup.vue';
  import FormulaEditor from '@/components/inputs/FormulaEditor.vue';

  // Input components
  import TextInput from '@/components/inputs/TextInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import InplaceEdit from '@/components/partials/InplaceEdit.vue';

  // UI components
  import Btn from '@/components/partials/Btn.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default {
    name: 'CrossOptionRulesEditor',

    components: {
      CPQConditionGroup,
      FormulaEditor,
      TextInput,
      NumberInput,
      SelectInput,
      ToggleInput,
      Btn,
      EmptyState,
      InplaceEdit,
      BaseIcon,
      draggable,
    },

    props: {
      modelValue: {
        type: Array,
        default: () => [],
      },
      productOptions: {
        type: Array,
        default: () => [],
        description: 'Available product options for condition building',
      },
      computedFields: {
        type: Array,
        default: () => [],
        description: 'Computed fields that can be used in conditions (e.g., Lengte × Breedte)',
      },
    },

    emits: ['update:modelValue'],

    setup(props, { emit }) {
      const localRules = ref([...props.modelValue]);
      const isUpdatingFromExternal = ref(false);
      // Track newly added rule for auto-focus
      const newlyAddedRuleId = ref(null);

      // Accordion state: rules are collapsed by default, newly added rules start expanded
      const expandedRules = reactive({});

      const isExpanded = (ruleId) => !!expandedRules[ruleId];

      const toggleRule = (ruleId) => {
        expandedRules[ruleId] = !expandedRules[ruleId];
      };

      // Watch for external changes
      watch(
        () => props.modelValue,
        (newRules) => {
          // Only update if the value actually changed
          const newRulesStr = JSON.stringify(newRules);
          const currentRulesStr = JSON.stringify(localRules.value);
          if (newRulesStr !== currentRulesStr) {
            isUpdatingFromExternal.value = true;
            localRules.value = [...newRules];
            // Reset flag after update cycle
            nextTick(() => {
              isUpdatingFromExternal.value = false;
            });
          }
        }
      );

      // Emit changes
      watch(
        localRules,
        (newRules) => {
          // Don't emit if we're updating from external source
          if (isUpdatingFromExternal.value) {
            return;
          }
          emit('update:modelValue', [...newRules]);
        },
        { deep: true }
      );

      // Convert product options to attributes format for CPQConditionGroup
      const availableAttributes = computed(() => {
        const attributes = [
          // Special attributes
          { key: 'total', label: 'Totaal bedrag', type: 'number' },
          { key: 'subtotal', label: 'Subtotaal (voor regels)', type: 'number' },
          { key: 'option_count', label: 'Aantal geselecteerde opties', type: 'number' },
        ];

        // Add product options
        props.productOptions.forEach((option) => {
          const isChoice =
            option.type === 'single-choice' ||
            option.type === 'multi-choice' ||
            option.type === 'choice';
          const choices = option.choiceConfig?.choices || option.options || [];

          if (isChoice && choices.length > 0) {
            attributes.push({
              key: option.id,
              label: option.label,
              type: 'select',
              options: choices.map((c) => ({
                id: c.id,
                value: c.value ?? c.id ?? c.label,
                label: c.label || c.value || c.name,
              })),
            });
          } else {
            attributes.push({
              key: option.id,
              label: option.label,
              type: option.type === 'number' ? 'number' : 'text',
            });
          }
        });

        // Add computed fields
        if (props.computedFields && Array.isArray(props.computedFields)) {
          props.computedFields.forEach((field) => {
            if (field.id && field.label) {
              attributes.push({
                key: field.id,
                label: `📊 ${field.label}`,
                type: 'number',
              });
            }
          });
        }

        return attributes;
      });

      // Action type options
      const actionTypeOptions = [
        { value: 'add', label: 'Bedrag toevoegen (€)' },
        { value: 'subtract', label: 'Bedrag aftrekken (€)' },
        { value: 'discount', label: 'Korting percentage (%)' },
        { value: 'multiply', label: 'Vermenigvuldigen met factor' },
        { value: 'override', label: 'Prijs overschrijven (€)' },
        { value: 'formula', label: 'Aangepaste formule' },
      ];

      // Helper functions
      const getActionValueLabel = (actionType) => {
        const labels = {
          add: 'Bedrag (€)',
          subtract: 'Bedrag (€)',
          discount: 'Percentage (%)',
          multiply: 'Factor',
          override: 'Nieuwe prijs (€)',
        };
        return labels[actionType] || 'Waarde';
      };

      const getActionValueMin = (actionType) => {
        if (actionType === 'multiply') return 0.0001;
        return 0;
      };

      const getActionValueMax = (actionType) => {
        if (actionType === 'discount') return 100;
        return null;
      };

      const getActionValueStep = (actionType) => {
        if (actionType === 'multiply') return 0.0001;
        if (actionType === 'discount') return 0.1;
        return 0.01;
      };

      const formatPercent = (value) =>
        new Intl.NumberFormat('nl-NL', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);

      const getActionHelperText = (action) => {
        const numericValue = Number(action?.value);
        if (Number.isNaN(numericValue)) return '';

        if (action?.type === 'multiply') {
          const percentageChange = (numericValue - 1) * 100;
          const direction = percentageChange >= 0 ? '+' : '-';
          return `${direction}${formatPercent(Math.abs(percentageChange))}% op huidig totaal`;
        }

        if (action?.type === 'discount') {
          return `${formatPercent(numericValue)}% korting op huidig totaal`;
        }

        return '';
      };

      const formatConditionValue = (attribute, value) => {
        if (value === null || value === undefined || value === '') {
          return '(leeg)';
        }

        if (attribute?.type === 'select' && Array.isArray(attribute.options)) {
          const matchedOption = attribute.options.find(
            (option) =>
              `${option.value}` === `${value}` ||
              `${option.id}` === `${value}` ||
              `${option.label}` === `${value}`
          );
          return matchedOption?.label || value;
        }

        return value;
      };

      const formatConditionPreview = (group) => {
        if (!group.rules?.length) return 'Geen voorwaarden';

        const ruleTexts = group.rules.map((rule) => {
          const attr = availableAttributes.value.find((a) => a.key === rule.attribute);
          const attrLabel = attr?.label || rule.attribute;
          const readableValue = formatConditionValue(attr, rule.value);
          return `${attrLabel} ${rule.operator} ${readableValue}`;
        });

        return ruleTexts.join(` ${group.logic} `);
      };

      // Formula fields for FormulaEditor
      const formulaFields = computed(() => {
        const fields = props.productOptions.map((option) => ({
          id: option.id,
          label: option.label,
          type: option.type === 'number' ? 'number' : 'single-choice',
          choices:
            option.type === 'single-choice' || option.type === 'multi-choice'
              ? option.choices
              : null,
        }));

        // Add computed fields
        if (props.computedFields && Array.isArray(props.computedFields)) {
          props.computedFields.forEach((field) => {
            if (field.id && field.label) {
              fields.push({
                id: field.id,
                label: `📊 ${field.label}`,
                type: 'number',
              });
            }
          });
        }

        // Add special fields
        fields.push(
          { id: 'total', label: 'Totaal bedrag', type: 'number' },
          { id: 'subtotal', label: 'Subtotaal', type: 'number' },
          { id: 'option_count', label: 'Aantal opties', type: 'number' }
        );

        return fields;
      });

      const formatActionPreview = (actions) => {
        if (!actions?.length) return 'Geen acties';

        return actions
          .map((action) => {
            switch (action.type) {
              case 'add':
                return `+€${action.value}`;
              case 'subtract':
                return `-€${action.value}`;
              case 'discount':
                return `${action.value}% korting`;
              case 'multiply':
                return `×${action.value}`;
              case 'override':
                return `= €${action.value}`;
              case 'formula':
                return `Formule: ${action.expression || '(leeg)'}`;
              default:
                return action.type;
            }
          })
          .join(', ');
      };

      // Rule management
      const addRule = () => {
        const ruleId = `rule_${uid()}`;
        const newRule = {
          id: ruleId,
          label: '',
          showInQuote: false,
          rootGroup: {
            logic: 'AND',
            rules: [{ attribute: 'total', operator: '>=', value: '' }],
            subGroups: [],
          },
          actions: [{ type: 'discount', value: 5 }],
        };

        localRules.value.push(newRule);

        // New rules start expanded so they can be configured immediately
        expandedRules[ruleId] = true;

        // Set as newly added for auto-focus
        newlyAddedRuleId.value = ruleId;
        // Clear after next tick so it doesn't re-focus on re-render
        nextTick(() => {
          setTimeout(() => {
            newlyAddedRuleId.value = null;
          }, 100);
        });
      };

      const removeRule = (index) => {
        localRules.value.splice(index, 1);
      };

      const addAction = (ruleIndex) => {
        localRules.value[ruleIndex].actions.push({
          type: 'add',
          value: 0,
        });
      };

      const removeAction = (ruleIndex, actionIndex) => {
        localRules.value[ruleIndex].actions.splice(actionIndex, 1);
      };

      return {
        localRules,
        availableAttributes,
        actionTypeOptions,
        getActionValueLabel,
        getActionValueMin,
        getActionValueMax,
        getActionValueStep,
        getActionHelperText,
        formatConditionPreview,
        formatActionPreview,
        formulaFields,
        addRule,
        removeRule,
        addAction,
        removeAction,
        newlyAddedRuleId,
        isExpanded,
        toggleRule,
      };
    },
  };
</script>

<style scoped lang="scss">
  .cross-option-rules-editor {
    .rules-list {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }

    .rules-draggable {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }

    .rule-card {
      @include rule-card;

      &--ghost {
        opacity: 0.5;
        border-style: dashed;
        border-color: var(--brand-300);
      }

      &--collapsed .rule-header {
        border-bottom: none;
      }

      .rule-header {
        @include rule-card-header;
        align-items: center;
        cursor: pointer;

        .rule-drag-handle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          padding: 0.5rem 0.25rem;
          margin-left: -0.25rem;
          color: var(--slate-400);
          cursor: grab;
          border-radius: 4px;

          @include hover {
            color: var(--slate-600);
            background: var(--slate-100);
          }

          &:active {
            cursor: grabbing;
          }
        }

        .rule-info {
          flex: 1;
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 1rem;
          align-items: end;
        }

        .rule-chevron {
          flex-shrink: 0;
          color: var(--slate-400);
          transition: transform 0.2s ease;
        }
      }

      &--collapsed .rule-header .rule-chevron {
        transform: rotate(180deg);
      }

      .rule-collapsed-summary {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        padding: 0.75rem 1rem;
        font-size: 0.875rem;
        color: var(--slate-600);
        cursor: pointer;

        strong {
          color: var(--slate-800);
        }

        &__condition,
        &__action {
          @include ellipsis(1);
        }
      }

      .rule-body {
        @include rule-card-body;
      }

      .rule-section {
        @include rule-card-section;

        h5 {
          @include rule-card-section-title;
        }
      }

      // .conditions-wrapper {
      //   padding: 0.75rem;
      //   background: var(--blue-50);
      //   border-radius: 6px;
      //   border: 1px solid var(--blue-200);
      // }

      .actions-list {
        .action-row {
          display: grid;
          grid-template-columns: minmax(240px, 300px) minmax(0, 1fr) auto;
          gap: 1rem;
          align-items: end;
          margin-bottom: 0.75rem;
          background-color: #ffffff;
          border: 1px solid var(--slate-200);
          padding: 0.5rem;
          border-radius: 0.5rem;

          &:last-child {
            margin-bottom: 1rem;
          }

          @include lg-down {
            grid-template-columns: 1fr auto;
          }
        }

        .action-type-select {
          min-width: 0;
        }

        .action-value-field {
          min-width: 0;
        }

        .action-value-input {
          min-width: 0;
        }

        .action-help {
          margin: 0.25rem 0 0;
          color: var(--slate-600);
          font-size: 0.8125rem;
          line-height: 1.3;
        }

        .formula-input-wrapper {
          grid-column: 1 / -1;
        }
      }

      .rule-preview {
        @include rule-card-preview;

        .preview-content {
          .preview-condition,
          .preview-action {
            margin-bottom: 0.5rem;

            &:last-child {
              margin-bottom: 0;
            }
          }
        }
      }
    }

    .add-rule-btn {
      margin-top: 0.5rem;
    }
  }
</style>
